import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getInitials } from '@/lib/utils';
import type { GlobalRole } from '@/types';

export interface WorkGroupInternalUserOption {
  id: string;
  email: string;
  displayName: string;
  publicName: string | null;
  avatarUrl: string | null;
  role: GlobalRole;
  isActive: boolean;
}

interface WorkGroupInternalUserPickerProps {
  id?: string;
  label?: string;
  value: string;
  onValueChange: (userId: string) => void;
  users: WorkGroupInternalUserOption[];
  excludedUserIds?: Iterable<string>;
  isLoading?: boolean;
  loadError?: boolean;
  placeholder?: string;
  disabled?: boolean;
  emptyMessage?: string;
  'data-testid'?: string;
}

function userLabel(user: WorkGroupInternalUserOption): string {
  return user.displayName?.trim() || user.email;
}

function PickerField({
  id,
  label,
  dataTestId,
  children,
}: {
  id: string;
  label: string;
  dataTestId: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2" data-testid={dataTestId}>
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

export function WorkGroupInternalUserPicker({
  id = 'wg-internal-user-picker',
  label = 'Internal user',
  value,
  onValueChange,
  users,
  excludedUserIds,
  isLoading = false,
  loadError = false,
  placeholder = 'Search by name or email…',
  disabled = false,
  emptyMessage = 'All internal users already have a permission on this folder.',
  'data-testid': dataTestId = 'wg-internal-user-picker',
}: WorkGroupInternalUserPickerProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const excludedIds = useMemo(
    () => new Set(excludedUserIds ? Array.from(excludedUserIds) : []),
    [excludedUserIds],
  );

  const internalUsers = useMemo(
    () => users.filter((u) => u.role !== 'client' && u.isActive),
    [users],
  );

  const availableUsers = useMemo(
    () => internalUsers.filter((u) => !excludedIds.has(u.id)),
    [internalUsers, excludedIds],
  );

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return availableUsers;
    return availableUsers.filter(
      (u) =>
        u.displayName.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query) ||
        (u.publicName?.toLowerCase().includes(query) ?? false),
    );
  }, [availableUsers, searchQuery]);

  const selectedUser = useMemo(
    () => internalUsers.find((u) => u.id === value) ?? users.find((u) => u.id === value),
    [internalUsers, users, value],
  );

  const handleSelect = (userId: string) => {
    onValueChange(userId);
    setOpen(false);
    setSearchQuery('');
  };

  if (loadError) {
    return (
      <PickerField id={id} label={label} dataTestId={dataTestId}>
        <p className="text-sm text-destructive">Could not load users.</p>
      </PickerField>
    );
  }

  if (isLoading) {
    return (
      <PickerField id={id} label={label} dataTestId={dataTestId}>
        <p className="text-sm text-muted-foreground">Loading users…</p>
      </PickerField>
    );
  }

  if (availableUsers.length === 0) {
    return (
      <PickerField id={id} label={label} dataTestId={dataTestId}>
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        <p className="text-xs text-muted-foreground">
          Client users cannot receive folder permissions. The backend rejects client assignments.
        </p>
      </PickerField>
    );
  }

  return (
    <div className="space-y-2" data-testid={dataTestId}>
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            {selectedUser ? (
              <span className="flex min-w-0 items-center gap-2">
                <Avatar className="h-5 w-5 shrink-0">
                  <AvatarImage src={selectedUser.avatarUrl || undefined} />
                  <AvatarFallback className="text-[10px]">
                    {getInitials(userLabel(selectedUser))}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{userLabel(selectedUser)}</span>
                <span className="truncate text-xs text-muted-foreground">{selectedUser.email}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <div className="flex items-center border-b px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={placeholder}
              className="h-10 border-0 focus-visible:ring-0"
              autoComplete="off"
            />
          </div>
          <ScrollArea className="max-h-[220px]">
            {filteredUsers.length === 0 ? (
              <p className="p-4 text-center text-sm text-muted-foreground">No users match your search.</p>
            ) : (
              <div className="p-1">
                {filteredUsers.map((user) => {
                  const isSelected = user.id === value;
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleSelect(user.id)}
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-muted"
                      aria-pressed={isSelected}
                    >
                      <Avatar className="h-6 w-6 shrink-0">
                        <AvatarImage src={user.avatarUrl || undefined} />
                        <AvatarFallback className="text-[10px]">
                          {getInitials(userLabel(user))}
                        </AvatarFallback>
                      </Avatar>
                      <UserListItem user={user} />
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function UserListItem({ user }: { user: WorkGroupInternalUserOption }) {
  return (
    <>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{userLabel(user)}</p>
        <p className="truncate text-xs text-muted-foreground">{user.email}</p>
      </div>
      <Badge variant="outline" className="shrink-0 font-normal capitalize">
        {user.role}
      </Badge>
    </>
  );
}
